import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ArrowRight, ShieldCheck, Building2, LineChart, Compass } from "lucide-react";
import i18n, { SUPPORTED_LANGUAGES } from "@/i18n";
import ContactForm from "@/components/ContactForm";

const HERO_IMG =
    "https://images.unsplash.com/photo-1610016302534-6f67f1c968d8?auto=format&fit=crop&w=1400&q=80";
const SHOWCASE_A =
    "https://images.unsplash.com/photo-1600607687644-c7171b42498f?auto=format&fit=crop&w=1200&q=80";
const SHOWCASE_B =
    "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?auto=format&fit=crop&w=1200&q=80";
const SHOWCASE_C =
    "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80";
const INTRO_IMG =
    "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1400&q=80";

function Eyebrow({ children }) {
    return <span className="aurx-eyebrow">{children}</span>;
}

function Hero() {
    const { t } = useTranslation();
    return (
        <section className="aurx-hero">
            <div className="aurx-container">
                <div className="aurx-hero-grid">
                    <div>
                        <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
                        <h1
                            className="aurx-display"
                            style={{ marginTop: 20, marginBottom: 24 }}
                        >
                            {t("hero.title")}
                        </h1>
                        <p className="p-lead" style={{ maxWidth: 560 }}>
                            {t("hero.subtitle")}
                        </p>
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                marginTop: 32,
                                flexWrap: "wrap",
                            }}
                        >
                            <a
                                href="#projects"
                                className="aurx-btn aurx-btn-primary"
                                data-testid="hero-cta-discover"
                            >
                                {t("cta.discover")}
                                <ArrowRight size={16} />
                            </a>
                            <a
                                href="#contact"
                                className="aurx-btn aurx-btn-ghost"
                                data-testid="hero-cta-contact"
                            >
                                {t("cta.contact")}
                            </a>
                        </div>
                        <div className="aurx-stats">
                            <div>
                                <span className="aurx-stat-value">
                                    {t("hero.stat1_value")}
                                </span>
                                <span className="aurx-stat-label">
                                    {t("hero.stat1_label")}
                                </span>
                            </div>
                            <div>
                                <span className="aurx-stat-value">
                                    {t("hero.stat2_value")}
                                </span>
                                <span className="aurx-stat-label">
                                    {t("hero.stat2_label")}
                                </span>
                            </div>
                            <div>
                                <span className="aurx-stat-value">
                                    {t("hero.stat3_value")}
                                </span>
                                <span className="aurx-stat-label">
                                    {t("hero.stat3_label")}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="aurx-hero-image">
                        <img
                            src={HERO_IMG}
                            alt={t("hero.image_alt")}
                            loading="eager"
                            width="800"
                            height="1000"
                        />
                        <div className="aurx-hero-badge">
                            <strong style={{ color: "var(--gold)" }}>
                                Milano · Torino · Napoli · Bologna
                            </strong>
                            <br />
                            {t("hero.stat2_label")}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function Intro() {
    const { t } = useTranslation();
    return (
        <section id="intro">
            <div className="aurx-container">
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 60,
                        alignItems: "center",
                    }}
                    className="aurx-intro-grid"
                >
                    <div>
                        <Eyebrow>{t("intro.eyebrow")}</Eyebrow>
                        <h2
                            className="aurx-h2"
                            style={{ marginTop: 20, marginBottom: 20 }}
                        >
                            {t("intro.title")}
                        </h2>
                        <p className="p-lead">{t("intro.body")}</p>
                    </div>
                    <div
                        style={{
                            aspectRatio: "5/4",
                            borderRadius: 24,
                            overflow: "hidden",
                            boxShadow:
                                "0 30px 60px -40px rgba(11, 27, 43, 0.35)",
                        }}
                    >
                        <img
                            src={INTRO_IMG}
                            alt="Nuovi complessi residenziali in una città italiana"
                            style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                            loading="lazy"
                        />
                    </div>
                </div>
                <style>{`
                    @media (max-width: 900px) {
                        .aurx-intro-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
                    }
                `}</style>
            </div>
        </section>
    );
}

function Why() {
    const { t } = useTranslation();
    const cards = t("why.cards", { returnObjects: true });
    const icons = {
        barriers: <Building2 size={28} />,
        digital: <Compass size={28} />,
        choice: <LineChart size={28} />,
        impact: <ShieldCheck size={28} />,
    };
    return (
        <section id="why" style={{ background: "#fff" }}>
            <div className="aurx-container">
                <Eyebrow>{t("why.eyebrow")}</Eyebrow>
                <h2
                    className="aurx-h2"
                    style={{ marginTop: 20, marginBottom: 40, maxWidth: 720 }}
                >
                    {t("why.title")}
                </h2>
                <div className="aurx-grid-4">
                    {Object.keys(cards).map((k) => (
                        <div key={k} className="aurx-card">
                            <div
                                style={{
                                    color: "var(--gold-2)",
                                    marginBottom: 16,
                                }}
                            >
                                {icons[k]}
                            </div>
                            <h3
                                style={{
                                    fontFamily: "Fraunces, serif",
                                    fontWeight: 500,
                                    fontSize: 20,
                                    margin: "0 0 10px",
                                }}
                            >
                                {cards[k].title}
                            </h3>
                            <p className="p-body" style={{ margin: 0 }}>
                                {cards[k].body}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function How() {
    const { t } = useTranslation();
    const steps = t("how.steps", { returnObjects: true });
    return (
        <section id="how" className="aurx-dark">
            <div className="aurx-container">
                <Eyebrow>{t("how.eyebrow")}</Eyebrow>
                <h2
                    className="aurx-h2"
                    style={{ marginTop: 20, marginBottom: 40, maxWidth: 720 }}
                >
                    {t("how.title")}
                </h2>
                <div>
                    {Object.keys(steps).map((k) => (
                        <div
                            key={k}
                            className="aurx-step"
                            style={{
                                borderTopColor: "rgba(246, 241, 234, 0.12)",
                            }}
                        >
                            <div className="aurx-step-num">
                                {steps[k].number}
                            </div>
                            <div className="aurx-step-body">
                                <h3 style={{ color: "var(--sand)" }}>
                                    {steps[k].title}
                                </h3>
                                <p
                                    style={{
                                        color: "rgba(246, 241, 234, 0.75)",
                                    }}
                                >
                                    {steps[k].body}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Showcase() {
    const { t } = useTranslation();
    const items = t("showcase.items", { returnObjects: true });
    const imgs = { a: SHOWCASE_A, b: SHOWCASE_B, c: SHOWCASE_C };
    return (
        <section id="projects">
            <div className="aurx-container">
                <Eyebrow>{t("showcase.eyebrow")}</Eyebrow>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        flexWrap: "wrap",
                        gap: 24,
                        marginTop: 20,
                        marginBottom: 40,
                    }}
                >
                    <h2 className="aurx-h2" style={{ maxWidth: 640 }}>
                        {t("showcase.title")}
                    </h2>
                    <p className="p-body" style={{ maxWidth: 380 }}>
                        {t("showcase.description")}
                    </p>
                </div>
                <div className="aurx-grid-3">
                    {Object.keys(items).map((k) => (
                        <div key={k} className="aurx-showcase-item">
                            <div className="aurx-showcase-img">
                                <img
                                    src={imgs[k]}
                                    alt={items[k].image_alt}
                                    loading="lazy"
                                />
                            </div>
                            <div className="aurx-showcase-body">
                                <span className="aurx-tag">
                                    {items[k].tag}
                                </span>
                                <h3
                                    style={{
                                        fontFamily: "Fraunces, serif",
                                        fontWeight: 500,
                                        fontSize: 22,
                                        margin: "4px 0 8px",
                                        color: "var(--ink)",
                                    }}
                                >
                                    {items[k].title}
                                </h3>
                                <p className="p-body" style={{ margin: 0 }}>
                                    {items[k].body}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function Trust() {
    const { t } = useTranslation();
    const points = t("trust.points", { returnObjects: true });
    return (
        <section id="trust" style={{ background: "var(--sand-2)" }}>
            <div className="aurx-container">
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 60,
                    }}
                    className="aurx-trust-grid"
                >
                    <div>
                        <Eyebrow>{t("trust.eyebrow")}</Eyebrow>
                        <h2
                            className="aurx-h2"
                            style={{ marginTop: 20, marginBottom: 24 }}
                        >
                            {t("trust.title")}
                        </h2>
                        <p className="p-lead">{t("trust.body")}</p>
                    </div>
                    <div
                        style={{
                            display: "grid",
                            gap: 16,
                            alignSelf: "center",
                        }}
                    >
                        {Object.keys(points).map((k) => (
                            <div
                                key={k}
                                style={{
                                    display: "flex",
                                    gap: 16,
                                    padding: "20px 24px",
                                    background: "#fff",
                                    borderRadius: 16,
                                    border: "1px solid var(--line)",
                                    alignItems: "center",
                                }}
                            >
                                <ShieldCheck
                                    size={22}
                                    color="var(--gold-2)"
                                />
                                <span
                                    style={{
                                        color: "var(--ink)",
                                        fontWeight: 500,
                                    }}
                                >
                                    {points[k]}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
                <style>{`
                    @media (max-width: 900px) {
                        .aurx-trust-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
                    }
                `}</style>
            </div>
        </section>
    );
}

function Contact() {
    const { t } = useTranslation();
    return (
        <section id="contact" style={{ background: "#fff" }}>
            <div className="aurx-container">
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 60,
                        alignItems: "center",
                    }}
                    className="aurx-contact-grid"
                >
                    <div>
                        <Eyebrow>{t("contact.eyebrow")}</Eyebrow>
                        <h2
                            className="aurx-h2"
                            style={{ marginTop: 20, marginBottom: 20 }}
                        >
                            {t("contact.title")}
                        </h2>
                        <p className="p-lead">{t("contact.description")}</p>
                        <div
                            style={{
                                marginTop: 32,
                                padding: 24,
                                background: "var(--sand)",
                                borderRadius: 20,
                            }}
                        >
                            <p
                                className="p-body"
                                style={{ margin: 0, lineHeight: 1.9 }}
                            >
                                <strong style={{ color: "var(--ink)" }}>
                                    Auronixra S.r.l.
                                </strong>
                                <br />
                                Via Melchiorre Gioia 26
                                <br />
                                20124 Milano (MI) · Italia
                                <br />
                                <a
                                    href="mailto:info@auronixra.com"
                                    style={{ color: "var(--gold-2)" }}
                                >
                                    info@auronixra.com
                                </a>
                            </p>
                        </div>
                    </div>
                    <ContactForm />
                </div>
                <style>{`
                    @media (max-width: 900px) {
                        .aurx-contact-grid { grid-template-columns: 1fr !important; gap: 40px !important; }
                    }
                `}</style>
            </div>
        </section>
    );
}

export default function HomePage() {
    const { lang: paramLang } = useParams();
    React.useEffect(() => {
        if (paramLang && SUPPORTED_LANGUAGES.includes(paramLang)) {
            i18n.changeLanguage(paramLang);
        }
    }, [paramLang]);
    return (
        <div data-testid="home-page">
            <Hero />
            <Intro />
            <Why />
            <How />
            <Showcase />
            <Trust />
            <Contact />
        </div>
    );
}
